//standard libraries
import { useEffect, useState } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";
//store-packages
import { StyledProgressPlaceholder } from "@hcl-commerce-store-sdk/react-component";
//local
import { INTERNAL_LINK } from "../../../constants/marketing";
import { StyledLink } from "../../../styled-mui";
import HTMLReactParser from "html-react-parser";
import { ImageLayout } from "../../elements/image/ImageLayout";
import { PUSHS_GET_REQUEST_ACTION } from "../../../redux/actions/pushs";
import { useDispatch, useSelector } from "react-redux";
import { pushsSelector } from "../../../redux/selectors/pushs";
import storeUtil from "../../../utils/storeUtil";

/**
 * Image wrapper with error handling
 * If image fails → parent will be removed
 */
const HtmlContainerPicture = ({
  image,
  isFirstImage,
  onError,
}: {
  image: string;
  isFirstImage?: boolean;
  onError: () => void;
}) => {
  return (
    <ImageLayout
      src={storeUtil.getUrlPicture(image, "")}
      alt=""
      styles={{ objectFit: "unset" }}
      className="push-image-card"
      priority={isFirstImage}
      showFallback={false}
      onLoadError={onError}
    />
  );
};

const PushHtml: any = ({ push, isFirstImage = false }) => {
  const [isImageValid, setIsImageValid] = useState(true);

  if (!isImageValid) {
    return null; // 🔥 hide parent completely
  }

  if (push.html) {
    return HTMLReactParser(push.html);
  } else if (push.picture) {
    const imageComponent = (
      <HtmlContainerPicture image={push.picture} isFirstImage={isFirstImage} onError={() => setIsImageValid(false)} />
    );

    if (push.link) {
      return push.link?.type === INTERNAL_LINK ? (
        <StyledLink to={push.link?.url}>{imageComponent}</StyledLink>
      ) : (
        <a href={push.link?.url}>{imageComponent}</a>
      );
    }

    return imageComponent;
  }

  return null;
};

export const useGenericPushWidget = (categoryIdentifiers) => {
  const dispatch = useDispatch();
  const [dataPushs, setDataPushs] = useState<any[] | null>(null);
  const { pushs } = useSelector(pushsSelector);

  useEffect(() => {
    !pushs && dispatch(PUSHS_GET_REQUEST_ACTION());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const adjustInvalidDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      const [year, month, day] = dateString.split("T")[0].split("-");
      const monthIndex = month - 1;
      const lastDayOfMonth = new Date(year, monthIndex + 1, 0).getDate();
      return new Date(year, monthIndex, Math.min(day, lastDayOfMonth));
    }
    return date;
  };

  useEffect(() => {
    let listPush: any[] = [];
    let listPushWithoutDoublon: any[] = [];

    if (pushs && pushs.length > 0 && categoryIdentifiers.length > 0) {
      listPush = pushs.filter((push) => {
        let find = false;
        const sDateStart = push.dateStart;
        const sDateEnd = push.dateEnd;
        const dateStart = new Date(sDateStart).getTime();
        const dateEnd = adjustInvalidDate(sDateEnd).getTime();
        const now = Date.now();

        if ((!sDateStart && !sDateEnd) || (dateStart <= now && dateEnd >= now)) {
          categoryIdentifiers.forEach((element) => {
            if (push.pageList.some((identifier) => identifier === element)) {
              find = true;
            }
          });
        }
        return find;
      });

      listPushWithoutDoublon = listPush.reduce((acc, current) => {
        const x = acc.find((item) => item.position === current.position);
        if (!x) {
          return acc.concat([current]);
        } else {
          const priorityIndex = acc.findIndex((item) => parseInt(item.priority) > parseInt(current.priority));
          if (priorityIndex > -1) {
            acc.splice(priorityIndex, 1);
            return acc.concat([current]);
          }
          return acc;
        }
      }, []);

      setDataPushs(listPushWithoutDoublon);
    }
  }, [pushs, categoryIdentifiers]);

  return {
    genericPushWidget:
      dataPushs !== null
        ? {
            loading: true,
            count: dataPushs.length,
            pushs: dataPushs.map((push, index) => ({
              html: (
                <LazyLoadComponent
                  key={index}
                  visibleByDefault={(window as any).__isPrerender__ || index === 1}
                  placeholder={<StyledProgressPlaceholder className="vertical-padding-20" />}>
                  <PushHtml push={push} isFirstImage={index === 1} />
                </LazyLoadComponent>
              ),
              priority: push.priority,
              position: push.position - 1,
            })),
          }
        : {
            loading: false,
            count: 0,
            pushs: [],
            position: -1,
          },
  };
};
